import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import cmsApi from '../services/cmsApi';
import { getImageUrl } from '../utils/getImageUrl';
import hardcodedFavicon from '../assets/new_logo_circle.png';

const defaults = {
  siteName: 'JR INSTITUTE OF INFORMATION TECHNOLOGY',
  siteTagline: 'ELIMU NA UJUZI',
  siteDescription: 'Leading institute for Information Technology education in Tanzania',
  logo: null,
  favicon: null,
  contactEmail: 'info@jriit.ac.tz',
  contactPhone: '+255 754 360 590',
  contactAddress: 'Sakina Area Arusha Tanzania, P. O. Box 7071 Namanga Road',
  contactPhone2: null,
  contactEmailAdmin: 'admin@jriit.ac.tz',
  contactEmailMarketing: null,
  contactHours: 'Monday - Friday: 8:00 AM - 5:00 PM',
  contactHoursSaturday: 'Saturday: 9:00 AM - 2:00 PM',
  contactHoursSunday: 'Sunday: Closed',
  contactHeroTitle: 'Get in Touch with Us',
  contactHeroSubtitle: "Have questions about our programs, admissions, or anything else? We'd love to hear from you. Reach out and we'll respond as soon as possible.",
  contactFormTitle: "Let's talk",
  contactFormDescription: 'To request information about our programs or schedule a campus visit, contact us directly or fill out the form and we will get back to you promptly.',
  contactMapTitle: 'Find Us on the Map',
  contactMapSubtitle: 'Visit our campus and experience the JRIIT difference firsthand',
  contactMapEmbedUrl: null,
  contactCtaTitle: 'Ready to Start Your Journey?',
  contactCtaDescription: 'Join thousands of students who have chosen JRIIT for their education. Contact us today to learn more about our programs and admission process.',
  contactCtaButton1Text: 'Apply Now',
  contactCtaButton1Link: '/admissions',
  contactCtaButton2Text: 'Learn More',
  contactCtaButton2Link: '/',
  facebookUrl: null,
  twitterUrl: null,
  instagramUrl: null,
  linkedinUrl: null,
  youtubeUrl: null,
  whatsappUrl: null,
  defaultMetaTitle: 'JRIIT - IT Education Tanzania',
  defaultMetaDescription: 'Leading institute for Information Technology education in Tanzania',
  copyrightText: '© {CURRENT_YEAR} {SITE_NAME}. All rights reserved.',
  primaryColor: '#5d0470',
  secondaryColor: '#9333ea',
  accentColor: '#fca903',
  fontFamily: 'System Default',
  fontSize: '16px',
  academicYear: '2025-2026',
  maintenanceMode: false,
  maintenanceMessage: null,
  announcementBanner: null,
  announcementLink: null,
  showAnnouncement: false,
};

const SiteSettingsContext = createContext(defaults);

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(defaults);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const result = await cmsApi.getGlobalSettings();
        if (!cancelled && result.success && result.settings) {
          setSettings(prev => ({
            ...prev,
            ...Object.fromEntries(
              Object.entries(result.settings).filter(([, v]) => v != null)
            ),
          }));
        }
      } catch (err) {
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const updateDocumentHead = useCallback((s) => {
    if (s.defaultMetaTitle || s.siteName) {
      document.title = s.defaultMetaTitle || s.siteName;
    }

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && (s.defaultMetaDescription || s.siteDescription)) {
      metaDesc.setAttribute('content', s.defaultMetaDescription || s.siteDescription);
    }

    {
      // Emergency hardcoded favicon: always prefer bundled asset for consistent rendering.
      const faviconUrl = hardcodedFavicon;

      const ensureLink = (selector, rel, attrs = {}) => {
        let link = document.querySelector(selector);
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', rel);
          document.head.appendChild(link);
        }
        Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
        link.setAttribute('href', faviconUrl);
      };

      ensureLink('link[rel="icon"][sizes="32x32"]', 'icon', { type: 'image/png', sizes: '32x32' });
      ensureLink('link[rel="icon"][sizes="16x16"]', 'icon', { type: 'image/png', sizes: '16x16' });
      ensureLink('link[rel="icon"]:not([sizes])', 'icon', { type: 'image/png' });
      ensureLink('link[rel="shortcut icon"]', 'shortcut icon', { type: 'image/x-icon' });
      ensureLink('link[rel="apple-touch-icon"]', 'apple-touch-icon');
      ensureLink('link[rel="apple-touch-icon"][sizes="192x192"]', 'apple-touch-icon', { sizes: '192x192' });
      ensureLink('link[rel="apple-touch-icon"][sizes="512x512"]', 'apple-touch-icon', { sizes: '512x512' });

      // Keep all existing icon-style links in sync (including defaults from index.html).
      const iconLinks = document.querySelectorAll('link[rel*="icon"]');
      iconLinks.forEach((link) => link.setAttribute('href', faviconUrl));
    }

    const root = document.documentElement;
    if (s.primaryColor) root.style.setProperty('--cms-primary', s.primaryColor);
    if (s.secondaryColor) root.style.setProperty('--cms-secondary', s.secondaryColor);
    if (s.accentColor) root.style.setProperty('--cms-accent', s.accentColor);
    if (s.fontSize) root.style.setProperty('--cms-font-size', s.fontSize);

    if (s.fontFamily) {
      const systemStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';

      if (s.fontFamily === 'System Default') {
        root.style.setProperty('--cms-font-family', systemStack);
        const existing = document.getElementById('cms-google-font');
        if (existing) existing.remove();
      } else {
        root.style.setProperty('--cms-font-family', `'${s.fontFamily}', ${systemStack}`);
        const linkId = 'cms-google-font';
        let link = document.getElementById(linkId);
        if (!link) {
          link = document.createElement('link');
          link.id = linkId;
          link.rel = 'stylesheet';
          document.head.appendChild(link);
        }
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(s.fontFamily)}:wght@300;400;500;600;700&display=swap`;
      }
    }
  }, []);

  useEffect(() => {
    updateDocumentHead(settings);
  }, [settings, updateDocumentHead]);

  return (
    <SiteSettingsContext.Provider value={settings}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}

export default SiteSettingsContext;
